import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CONFIG } from "../config";
import {
  buildHighRiskAlerts,
  buildRiskKpis,
} from "../utils/dashboardRisk";
import { buildAgeDistribution } from "../utils/dashboardAgeGroups";
import { buildProductivityTrend } from "../utils/dashboardProductivity";
import { buildTestUsageCounts } from "../utils/dashboardTestUsage";
import { buildAverageTestDurations } from "../utils/dashboardTestDuration";
import AgeDistributionChart from "../components/AgeDistributionChart";
import ProductivityTrendChart from "../components/ProductivityTrendChart";
import TestUsageDonutChart from "../components/TestUsageDonutChart";
import AverageTestDurationChart from "../components/AverageTestDurationChart";
import "../css/dashboard.css";

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const buildRecentPatientsFromExpedientes = (patients) =>
  patients
    .filter((patient) => Array.isArray(patient.tests) && patient.tests.length > 0)
    .map((patient) => {
      const latestTest = [...patient.tests].sort(
        (a, b) => new Date(b.testDay) - new Date(a.testDay)
      )[0];

      return {
        patientId: patient.id,
        patientName: patient.name,
        lastTestDay: latestTest.testDay,
        lastTestName: latestTest.testName,
      };
    })
    .sort((a, b) => new Date(b.lastTestDay) - new Date(a.lastTestDay));

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const getSeverityClass = (severity) => {
  const label = String(severity || "").toLowerCase();
  if (label.includes("severa") || label.includes("grave")) {
    return "dashboard-alert-card__badge--severe";
  }
  return "dashboard-alert-card__badge--moderate";
};

export default function Dashboard() {
  const [recentPatients, setRecentPatients] = useState([]);
  const [highRiskAlerts, setHighRiskAlerts] = useState([]);
  const [ageDistribution, setAgeDistribution] = useState({
    total: 0,
    groups: [],
  });
  const [productivityTrend, setProductivityTrend] = useState({
    monthly: { year: new Date().getFullYear(), total: 0, points: [] },
    weekly: { year: new Date().getFullYear(), total: 0, points: [] },
  });
  const [testUsage, setTestUsage] = useState({ total: 0, items: [] });
  const [testDurations, setTestDurations] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const riskKpis = useMemo(() => buildRiskKpis(highRiskAlerts), [highRiskAlerts]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const res = await axios.get(
          `${CONFIG.BACKEND_URL}/backend/getpatientswithtests`,
          { headers: authHeaders() }
        );
        const patients = Array.isArray(res.data) ? res.data : [];
        setRecentPatients(buildRecentPatientsFromExpedientes(patients));
        setHighRiskAlerts(buildHighRiskAlerts(patients));
        setAgeDistribution(buildAgeDistribution(patients));
        setProductivityTrend(buildProductivityTrend(patients));
        setTestUsage(buildTestUsageCounts(patients));
        setTestDurations(buildAverageTestDurations(patients));
      } catch (fetchError) {
        console.error("Error al cargar datos del dashboard:", fetchError);
        setError("No se pudieron cargar los datos del dashboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="dashboard-page">
      <div className="dashboard-top-row">
        <aside className="dashboard-recent-panel">
          <header className="dashboard-recent-header">
            <h2 className="dashboard-recent-title">Últimos pacientes evaluados</h2>
            <p className="dashboard-recent-subtitle">
              Ordenados por la fecha de su última prueba
            </p>
          </header>

          {loading ? (
            <p className="dashboard-recent-loading">Cargando pacientes...</p>
          ) : error ? (
            <p className="dashboard-recent-empty">{error}</p>
          ) : recentPatients.length === 0 ? (
            <p className="dashboard-recent-empty">
              Aún no hay pacientes con pruebas registradas.
            </p>
          ) : (
            <ul className="dashboard-recent-list">
              {recentPatients.map((patient) => (
                <li key={patient.patientId} className="dashboard-recent-item">
                  <div className="dashboard-recent-row">
                    <span className="dashboard-recent-name">
                      {patient.patientName}
                    </span>
                    <span className="dashboard-recent-meta">
                      Última prueba: {formatDate(patient.lastTestDay)}
                    </span>
                    {patient.lastTestName && (
                      <span className="dashboard-recent-test">
                        {patient.lastTestName}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <div className="dashboard-top-main">
          <header className="dashboard-main-header">
            <h1 className="dashboard-main-title">Dashboard</h1>
            <p className="dashboard-main-subtitle">
              Pacientes con puntajes por encima del corte de riesgo en evaluaciones
              recientes de Anxiety Beck, Depression Beck o Yesavage.
            </p>
          </header>

          <div className="dashboard-kpi-grid">
            <article className="dashboard-kpi-card dashboard-kpi-card--total">
              <span className="dashboard-kpi-label">Alertas totales</span>
              <strong className="dashboard-kpi-value">{riskKpis.total}</strong>
            </article>
            <article className="dashboard-kpi-card">
              <span className="dashboard-kpi-label">Anxiety Beck</span>
              <strong className="dashboard-kpi-value">{riskKpis.anxietyBeck}</strong>
            </article>
            <article className="dashboard-kpi-card">
              <span className="dashboard-kpi-label">Depression Beck</span>
              <strong className="dashboard-kpi-value">
                {riskKpis.depressionBeck}
              </strong>
            </article>
            <article className="dashboard-kpi-card">
              <span className="dashboard-kpi-label">Yesavage</span>
              <strong className="dashboard-kpi-value">{riskKpis.yesavage}</strong>
            </article>
          </div>

          <div className="dashboard-alerts-section">
            <div className="dashboard-alerts-header">
              <h2 className="dashboard-alerts-title">Pacientes en riesgo elevado</h2>
              <p className="dashboard-alerts-subtitle">
                Basado en la evaluación más reciente de cada prueba por paciente.
                Si hubo varias el mismo día, se muestra la de mayor puntaje.
              </p>
            </div>

            {loading ? (
              <p className="dashboard-alerts-empty">Cargando alertas...</p>
            ) : error ? (
              <p className="dashboard-alerts-empty">{error}</p>
            ) : highRiskAlerts.length === 0 ? (
              <p className="dashboard-alerts-empty">
                No hay pacientes con puntajes moderados o graves en estas pruebas.
              </p>
            ) : (
              <div className="dashboard-alerts-grid">
                {highRiskAlerts.map((alert) => (
                  <article
                    key={`${alert.patientId}-${alert.testKey}`}
                    className="dashboard-alert-card"
                  >
                    <div className="dashboard-alert-card__top">
                      <span className="dashboard-alert-card__patient">
                        {alert.patientName}
                      </span>
                      <span
                        className={`dashboard-alert-card__badge ${getSeverityClass(
                          alert.severity
                        )}`}
                      >
                        {alert.severity}
                      </span>
                    </div>
                    <p className="dashboard-alert-card__test">{alert.testName}</p>
                    <div className="dashboard-alert-card__meta">
                      <span>Puntaje: {alert.score}</span>
                      <span>{formatDate(alert.testDay)}</span>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-bottom-sections">
        <AverageTestDurationChart durations={testDurations} loading={loading} />
        <AgeDistributionChart distribution={ageDistribution} loading={loading} />
        <ProductivityTrendChart trend={productivityTrend} loading={loading} />
        <TestUsageDonutChart usage={testUsage} loading={loading} />
      </div>
    </div>
  );
}
